/**
 * Landing on the interviews list (refresh, link, bookmark), the list could not
 * fetch anything until the shell had loaded the user, permissions and saved
 * filters, and then its lazy chunk — on staging the request started ~4s in.
 * Here both the chunk and the last first-page request start during boot,
 * alongside the bootstrap calls.
 *
 * The chunk import must match the `InterviewRevampList` route in
 * `components/getRoutes` so both resolve to one chunk.
 */
import { store } from '@/store';
import { interviewAtsApi } from '@/store/query/interviews';
import {
  markBootPrefetch,
  readLastListRequest,
} from '@/pages/interviews/list/lastListRequest';

let requested = false;

const LIST_PATH = /^\/interviews(\/(all|past|upcoming))?\/?$/;

export const isInterviewsListPath = (pathname: string): boolean =>
  LIST_PATH.test(pathname);

// Same tab id the list derives from the URL (`/interviews` → "interviews").
const tabFromPath = (pathname: string): string =>
  pathname.split('/').pop() || 'all';

export const preloadInterviewsListRoute = (
  location: Pick<Location, 'pathname' | 'search'> = window.location
): void => {
  if (requested) return;
  requested = true;

  import('@/pages/interviews/list').catch(() => {
    // A failed preload must not surface — React.lazy retries on navigation.
    requested = false;
  });

  // A searched entry builds a different request, and without a session the
  // call would only bounce off auth.
  if (new URLSearchParams(location.search).has('search')) return;
  try {
    if (!localStorage.getItem('apiv1Auth')) return;
  } catch {
    return;
  }

  const params = readLastListRequest(tabFromPath(location.pathname));
  if (!params) return;

  markBootPrefetch(params);
  const request = store.dispatch(
    interviewAtsApi.endpoints.getAllInterview.initiate({ params })
  );
  // Hold the entry only until it settles; RTK keeps it cached for the list.
  request.then(
    () => request.unsubscribe(),
    () => request.unsubscribe()
  );
};
